import { useState, useEffect, useCallback, useContext, createContext } from 'react';

// ─── Shape ──────────────────────────────────────────────────────────────────
export interface AppContext {
  companyId: number | null;
  branchId: number | null;
  branchCode?: string | null;
  financialYearId: number | null;
  branchName: string | null;
  financialYearName: string | null;
  fyStartDate: string | null;
  fyEndDate: string | null;
}

const STORAGE_KEY = 'crm_app_context';
const SELECTED_UNIT_KEY = 'crm_selected_unit';
const EVENT_NAME = 'app-context-changed';

// ─── Helpers (synchronous, usable outside React) ────────────────────────────
function readContext(): AppContext {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AppContext>;
      const unit = readSelectedUnit();
      // Backfill missing branch metadata from selected unit cache.
      return {
        ...defaults(),
        ...parsed,
        branchId: parsed.branchId ?? unit?.id ?? null,
        branchCode: parsed.branchCode ?? unit?.unitCode ?? null,
        branchName: parsed.branchName ?? unit?.unitName ?? null,
      };
    }
  } catch { /* ignore */ }
  const unit = readSelectedUnit();
  return {
    ...defaults(),
    branchId: unit?.id ?? null,
    branchCode: unit?.unitCode ?? null,
    branchName: unit?.unitName ?? null,
  };
}

function readSelectedUnit(): { id?: number; unitCode?: string; unitName?: string } | null {
  try {
    const raw = localStorage.getItem(SELECTED_UNIT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function defaults(): AppContext {
  let companyId: number | null = null;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u?.companyId) companyId = u.companyId;
    }
  } catch { /* ignore */ }
  return {
    companyId,
    branchId: null,
    branchCode: null,
    financialYearId: null,
    branchName: null,
    financialYearName: null,
    fyStartDate: null,
    fyEndDate: null,
  };
}

/** Synchronous read — for use outside React (e.g. API interceptor). */
export function getAppContext(): AppContext {
  return readContext();
}

// ─── Transient "Full Report" toggle (Compliance reports) ────────────────────
// In-memory only (deliberately NOT persisted): when true, Compliance report
// downloads include fully-absent employees too. The API request interceptor
// forwards it as the X-Full-Report header; the backend only reads it while
// generating reports, so it is harmless on other requests. Resets to off on
// page reload.
let _fullReport = false;
export function setFullReport(on: boolean): void { _fullReport = on; }
export function getFullReport(): boolean { return _fullReport; }

/** Write a partial update and notify listeners. */
export function setAppContext(partial: Partial<AppContext>): void {
  const current = readContext();
  const next = { ...current, ...partial };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event(EVENT_NAME));
}

// ─── React Context ──────────────────────────────────────────────────────────
const Ctx = createContext<AppContext>(defaults());

export function useAppContext(): AppContext {
  const ctx = useContext(Ctx);
  return ctx;
}

/** React hook — re-renders when app context changes. */
export function useAppContextState(): AppContext {
  const [ctx, setCtx] = useState<AppContext>(readContext);

  const refresh = useCallback(() => setCtx(readContext()), []);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, refresh);
    // Also listen to legacy events for backward compat
    window.addEventListener('unit-changed', refresh);
    window.addEventListener('financial-year-updated', refresh);
    window.addEventListener('company-updated', refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener('unit-changed', refresh);
      window.removeEventListener('financial-year-updated', refresh);
      window.removeEventListener('company-updated', refresh);
    };
  }, [refresh]);

  return ctx;
}

export { Ctx as AppContextReact };
